import PropTypes from "prop-types";
import {
  labelPropType,
  disabledPropType,
  requiredPropType,
  onChangePropType,
  onSelectPropType,
  valuePropType,
  messagePropType,
} from "../../common";
import { filterOptionShape } from "../../shapes";

/**
 * PropType for an array of values
 * @type {PropTypes.Requireable<Array>}
 */
const valueArrayPropType = PropTypes.arrayOf(valuePropType);

/**
 * PropTypes for Select component
 *
 * Provides validation for the custom Select component supporting single and multiple selection modes.
 *
 * @property {Array<Object>} options - Array of selectable options. Each option should match filterOptionShape.
 * @property {Array} selected - Array of currently selected values (used in multiple selection mode).
 * @property {string|number|Array} value - Current value(s) of the select. Single value for standard select, array for multi-select.
 * @property {Function} onChange - Callback fired when selection changes. Receives the new value(s) as argument.
 * @property {Function} onSelected - Alternative callback for selection events. Receives the selected item(s).
 * @property {string} label - Main label text displayed for the select component.
 * @property {string} listLabel - Label text displayed for the options list/dropdown.
 * @property {boolean} disabled - When true, disables the select component and prevents user interaction.
 * @property {boolean} required - When true, marks the field as required for form validation.
 * @property {boolean} unique - When true, enforces unique values in multiple selection mode.
 * @property {boolean|string} valid - Validation state. True/false for valid/invalid, or string for custom validation messages.
 * @property {string} message - Validation or helper message displayed below the select component.
 * @property {string} controlHeight - Optional height override for the select control (e.g. "3rem").
 * @property {string} controlPadding - Optional padding override for the select input area.
 * @property {string} selectedListHeight - Optional height override for the selected list container.
 * @property {string} selectedListPadding - Optional padding override for the selected list container.
 */
export const selectPropTypes = {
  options: PropTypes.arrayOf(filterOptionShape),
  selected: valueArrayPropType,
  value: PropTypes.oneOfType([valuePropType, valueArrayPropType]),
  onChange: onChangePropType,
  onSelected: onSelectPropType,
  label: labelPropType,
  listLabel: labelPropType,
  disabled: disabledPropType,
  required: requiredPropType,
  unique: PropTypes.bool,
  valid: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  message: messagePropType,
  controlHeight: PropTypes.string,
  controlPadding: PropTypes.string,
  selectedListHeight: PropTypes.string,
  selectedListPadding: PropTypes.string,
};
